import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { Box, Typography } from '@mui/material';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import KeyIcon from '@mui/icons-material/Key';
import { MAX_VISIBLE_COLUMNS, NODE_WIDTH, tableLabel } from './modelGraphData';

// Colores del admin (AdminContentWrapper / menú lateral).
const ACCENT = 'var(--wb-accent)';
const TEXT = 'var(--wb-text)';
const MUTED = 'var(--wb-muted-75)';

// Algo más grandes que el default: desde aquí se arrastra para crear una relación.
const handleSx = { width: 12, height: 12, background: ACCENT, border: '2px solid var(--wb-bg-surface)' };

/**
 * Tabla del modelo semántico: nombre de negocio, nombre físico y sus
 * columnas (las primeras MAX_VISIBLE_COLUMNS; las que participan en un
 * join se resaltan con una llave).
 */
const ModelTableNode = ({ data, selected, sourcePosition = Position.Right, targetPosition = Position.Left }) => {
  const { table, joinColumns } = data;
  const columns = table.columns || [];
  const visible = columns.slice(0, MAX_VISIBLE_COLUMNS);
  const hidden = columns.length - visible.length;

  return (
    <Box
      sx={{
        width: NODE_WIDTH,
        backgroundColor: 'var(--wb-bg-surface)',
        borderRadius: 2,
        border: `1px solid ${selected ? ACCENT : 'var(--wb-x-border-94a3b838, rgba(148, 163, 184, 0.22))'}`,
        boxShadow: selected ? '0 0 0 3px var(--wb-x-text-38bdf840, rgba(56, 189, 248, 0.25))' : '0 8px 20px var(--wb-x-text-02061773, rgba(2, 6, 23, 0.45))',
        overflow: 'hidden',
        cursor: 'pointer'
      }}
    >
      <Handle type="target" position={targetPosition} style={handleSx} />
      <Box sx={{ px: 1.5, py: 1, backgroundColor: 'var(--wb-x-bg-0ea5e924, rgba(14, 165, 233, 0.14))', borderBottom: '1px solid var(--wb-border-16)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <TableChartOutlinedIcon sx={{ fontSize: 16, color: ACCENT }} />
          <Typography noWrap sx={{ color: 'var(--wb-text-title)', fontWeight: 600, fontSize: '0.85rem' }} title={tableLabel(table)}>
            {tableLabel(table)}
          </Typography>
        </Box>
        <Typography noWrap sx={{ color: MUTED, fontSize: '0.7rem', fontFamily: 'ui-monospace, Menlo, monospace' }} title={table.table_name}>
          {table.table_name}
        </Typography>
      </Box>
      <Box sx={{ py: 0.5 }}>
        {visible.map((column) => {
          const isJoin = joinColumns?.has(column.column_name);
          return (
            <Box
              key={column.column_name}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, height: 24 }}
              title={column.business_name || column.column_name}
            >
              <Box sx={{ width: 12, display: 'flex' }}>
                {isJoin && <KeyIcon sx={{ fontSize: 12, color: 'var(--wb-warning)' }} />}
              </Box>
              <Typography noWrap sx={{ flex: 1, color: isJoin ? 'var(--wb-x-text-fde68a, #fde68a)' : TEXT, fontSize: '0.75rem' }}>
                {column.column_name}
              </Typography>
              <Typography noWrap sx={{ color: MUTED, fontSize: '0.68rem', maxWidth: 90 }}>
                {column.data_type}
              </Typography>
            </Box>
          );
        })}
        {hidden > 0 && (
          <Typography sx={{ color: MUTED, fontSize: '0.7rem', px: 1.5, py: 0.25 }}>
            +{hidden} columna{hidden === 1 ? '' : 's'} más
          </Typography>
        )}
        {columns.length === 0 && (
          <Typography sx={{ color: MUTED, fontSize: '0.72rem', px: 1.5, py: 0.5 }}>Sin columnas</Typography>
        )}
      </Box>
      <Handle type="source" position={sourcePosition} style={handleSx} />
    </Box>
  );
};

export default memo(ModelTableNode);
